import {
  collection,
  doc,
  setDoc,
  addDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  deleteDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { format } from 'date-fns';

// Helper to get date ID in YYYY-MM-DD format
export const getDateId = (date = new Date()) => {
  return format(date, 'yyyy-MM-dd');
};

// ============================================
// STEPS & HEART POINTS
// ============================================

export const saveSteps = async (userId, date, steps) => {
  const dateId = getDateId(date);
  const docRef = doc(db, 'users', userId, 'steps', dateId);
  await setDoc(docRef, {
    steps,
    date: date.toISOString(),
    dateId,
    updatedAt: new Date().toISOString()
  }, { merge: true });
};

export const getSteps = async (userId, date) => {
  const dateId = getDateId(date);
  const docRef = doc(db, 'users', userId, 'steps', dateId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? docSnap.data() : null;
};

export const saveHeartPoints = async (userId, date, points) => {
  const dateId = getDateId(date);
  const docRef = doc(db, 'users', userId, 'heart_points', dateId);
  await setDoc(docRef, {
    points,
    date: date.toISOString(),
    dateId,
    updatedAt: new Date().toISOString()
  }, { merge: true });
};

export const getHeartPoints = async (userId, date) => {
  const dateId = getDateId(date);
  const docRef = doc(db, 'users', userId, 'heart_points', dateId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? docSnap.data() : null;
};

// Get steps for a date range
export const getStepsInRange = async (userId, startDate, endDate) => {
  const startDateId = getDateId(startDate);
  const endDateId = getDateId(endDate);
  
  const q = query(
    collection(db, 'users', userId, 'steps'),
    where('dateId', '>=', startDateId),
    where('dateId', '<=', endDateId),
    orderBy('dateId', 'asc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// Get heart points for a date range
export const getHeartPointsInRange = async (userId, startDate, endDate) => {
  const startDateId = getDateId(startDate);
  const endDateId = getDateId(endDate);
  
  const q = query(
    collection(db, 'users', userId, 'heart_points'),
    where('dateId', '>=', startDateId),
    where('dateId', '<=', endDateId),
    orderBy('dateId', 'asc')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getActivityHistory = async (userId, collectionName, days = 30) => {
  const q = query(
    collection(db, 'users', userId, collectionName),
    orderBy('date', 'desc'),
    limit(days)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// ============================================
// WATER TRACKING
// ============================================

export const addWaterEntry = async (userId, amount) => {
  const now = new Date();
  const dateId = getDateId(now);
  const entryId = `${dateId}_${now.getTime()}`;
  
  const docRef = doc(db, 'users', userId, 'water', entryId);
  await setDoc(docRef, {
    amount,
    date: now.toISOString(),
    dateId,
    hour: now.getHours(),
    createdAt: now.toISOString()
  });
  
  return entryId;
};

export const getWaterForDate = async (userId, date) => {
  const dateId = getDateId(date);
  const q = query(
    collection(db, 'users', userId, 'water'),
    where('dateId', '==', dateId),
    orderBy('createdAt', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getWaterInRange = async (userId, startDate, endDate) => {
  const startDateId = getDateId(startDate);
  const endDateId = getDateId(endDate);
  
  const q = query(
    collection(db, 'users', userId, 'water'),
    where('dateId', '>=', startDateId),
    where('dateId', '<=', endDateId),
    orderBy('dateId', 'asc'),
    orderBy('createdAt', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const deleteWaterEntry = async (userId, entryId) => {
  const docRef = doc(db, 'users', userId, 'water', entryId);
  await deleteDoc(docRef);
};

// ============================================
// MEALS
// ============================================

export const addMeal = async (userId, mealData) => {
  const now = new Date();
  const dateId = getDateId(now);
  const mealId = `${dateId}_${now.getTime()}`;
  
  const docRef = doc(db, 'users', userId, 'meals', mealId);
  await setDoc(docRef, {
    ...mealData,
    date: now.toISOString(),
    dateId,
    createdAt: now.toISOString()
  });
  
  return mealId;
};

export const getMealsForDate = async (userId, date) => {
  const dateId = getDateId(date);
  const q = query(
    collection(db, 'users', userId, 'meals'),
    where('dateId', '==', dateId),
    orderBy('createdAt', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getMealsInRange = async (userId, startDate, endDate) => {
  const startDateId = getDateId(startDate);
  const endDateId = getDateId(endDate);
  
  const q = query(
    collection(db, 'users', userId, 'meals'),
    where('dateId', '>=', startDateId),
    where('dateId', '<=', endDateId),
    orderBy('dateId', 'asc'),
    orderBy('createdAt', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getRecentMeals = async (userId, limitCount = 10) => {
  const q = query(
    collection(db, 'users', userId, 'meals'),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const deleteMeal = async (userId, mealId) => {
  const docRef = doc(db, 'users', userId, 'meals', mealId);
  await deleteDoc(docRef);
};

export const updateMeal = async (userId, mealId, mealData) => {
  const docRef = doc(db, 'users', userId, 'meals', mealId);
  await updateDoc(docRef, {
    ...mealData,
    updatedAt: new Date().toISOString()
  });
};

// ============================================
// WEIGHT
// ============================================

export const addWeightEntry = async (userId, weight, notes = '') => {
  const now = new Date();
  const entryId = now.getTime().toString();
  
  const docRef = doc(db, 'users', userId, 'weights', entryId);
  await setDoc(docRef, {
    weight,
    notes,
    date: now.toISOString(),
    createdAt: now.toISOString()
  });
  
  return entryId;
};

export const getWeightHistory = async (userId, limitCount = 90) => {
  const q = query(
    collection(db, 'users', userId, 'weights'),
    orderBy('date', 'desc'),
    limit(limitCount)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const deleteWeightEntry = async (userId, entryId) => {
  const docRef = doc(db, 'users', userId, 'weights', entryId);
  await deleteDoc(docRef);
};

// ============================================
// USER SETTINGS
// ============================================

export const getUserSettings = async (userId) => {
  const docRef = doc(db, 'users', userId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? docSnap.data() : null;
};

export const updateUserSettings = async (userId, settings) => {
  const docRef = doc(db, 'users', userId);
  await setDoc(docRef, {
    ...settings,
    updatedAt: new Date().toISOString()
  }, { merge: true });
};

// ============================================
// HABITS (Drinking & Smoking Tracking)
// ============================================

export const addHabitEntry = async (userId, entryData) => {
  const now = new Date();
  const dateId = getDateId(now);
  const entryId = `${dateId}_${now.getTime()}`;
  
  const docRef = doc(db, 'users', userId, 'habits', entryId);
  await setDoc(docRef, {
    ...entryData,
    date: now.toISOString(),
    dateId,
    createdAt: now.toISOString()
  });
  
  return entryId;
};

export const getHabitEntriesForDate = async (userId, date) => {
  const dateId = getDateId(date);
  const q = query(
    collection(db, 'users', userId, 'habits'),
    where('dateId', '==', dateId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getHabitHistory = async (userId, days = 30) => {
  const q = query(
    collection(db, 'users', userId, 'habits'),
    orderBy('createdAt', 'desc'),
    limit(days * 10) // Approximate, assuming max 10 entries per day
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const deleteHabitEntry = async (userId, entryId) => {
  const docRef = doc(db, 'users', userId, 'habits', entryId);
  await deleteDoc(docRef);
};

// ============================================
// FOODS DATABASE (Indian Foods Management)
// ============================================

// Add a new food item to the global foods collection
export const addFoodItem = async (foodData) => {
  const foodsRef = collection(db, 'foods');
  const docRef = await addDoc(foodsRef, {
    ...foodData,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });
  return docRef.id;
};

// Update an existing food item
export const updateFoodItem = async (foodId, foodData) => {
  const docRef = doc(db, 'foods', foodId);
  await updateDoc(docRef, {
    ...foodData,
    updatedAt: new Date().toISOString()
  });
};

// Delete a food item
export const deleteFoodItem = async (foodId) => {
  const docRef = doc(db, 'foods', foodId);
  await deleteDoc(docRef);
};

// Get all food items (with optional search filter)
export const getAllFoods = async (searchTerm = '') => {
  const foodsRef = collection(db, 'foods');
  let q = query(foodsRef, orderBy('name', 'asc'));
  
  const snapshot = await getDocs(q);
  let foods = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  
  // Client-side filtering for search (since Firestore doesn't support contains)
  if (searchTerm) {
    const searchLower = searchTerm.toLowerCase();
    foods = foods.filter(food => 
      food.name.toLowerCase().includes(searchLower) ||
      food.category?.toLowerCase().includes(searchLower)
    );
  }
  
  return foods;
};

// Search foods by name (for autocomplete)
export const searchFoodsByName = async (searchTerm) => {
  if (!searchTerm || searchTerm.length < 2) return [];
  
  const foodsRef = collection(db, 'foods');
  const snapshot = await getDocs(foodsRef);
  
  const searchLower = searchTerm.toLowerCase();
  return snapshot.docs
    .map(doc => ({ id: doc.id, ...doc.data() }))
    .filter(food => food.name.toLowerCase().includes(searchLower))
    .slice(0, 10); // Limit to 10 results for autocomplete
};

// Get a single food item by ID
export const getFoodById = async (foodId) => {
  const docRef = doc(db, 'foods', foodId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
};

// Bulk import foods (for initial setup)
export const bulkImportFoods = async (foodsArray) => {
  const batch = [];
  const foodsRef = collection(db, 'foods');
  
  for (const food of foodsArray) {
    const docRef = doc(foodsRef);
    batch.push(
      setDoc(docRef, {
        ...food,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
    );
  }
  
  // Execute all in parallel (Firebase handles batching internally)
  await Promise.all(batch);
  return batch.length;
};

// ============================================
// SLEEP TRACKING
// ============================================

export const addSleepEntry = async (userId, sleepData) => {
  const now = new Date();
  // Use wake time for dateId so sleep appears on the day you woke up
  const wakeDate = new Date(sleepData.wakeTime);
  const dateId = getDateId(wakeDate);
  const sleepId = `${dateId}_${now.getTime()}`;
  
  const docRef = doc(db, 'users', userId, 'sleep', sleepId);
  await setDoc(docRef, {
    ...sleepData,
    date: wakeDate.toISOString(),
    dateId,
    createdAt: now.toISOString()
  });
  
  return sleepId;
};

export const getSleepForDate = async (userId, date) => {
  const dateId = getDateId(date);
  const q = query(
    collection(db, 'users', userId, 'sleep'),
    where('dateId', '==', dateId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getSleepInRange = async (userId, startDate, endDate) => {
  const startDateId = getDateId(startDate);
  const endDateId = getDateId(endDate);
  
  const q = query(
    collection(db, 'users', userId, 'sleep'),
    where('dateId', '>=', startDateId),
    where('dateId', '<=', endDateId),
    orderBy('dateId', 'asc'),
    orderBy('createdAt', 'asc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const deleteSleepEntry = async (userId, entryId) => {
  const docRef = doc(db, 'users', userId, 'sleep', entryId);
  await deleteDoc(docRef);
};

// ============================================
// JOURNAL
// ============================================

export const addJournalEntry = async (userId, journalData) => {
  const now = new Date();
  // Use entry date for dateId so journal appears on the correct day
  const entryDate = new Date(journalData.entryDate);
  const dateId = getDateId(entryDate);
  const journalId = `${dateId}_${now.getTime()}`;
  
  const docRef = doc(db, 'users', userId, 'journal', journalId);
  await setDoc(docRef, {
    ...journalData,
    dateId,
    createdAt: now.toISOString(),
    updatedAt: now.toISOString()
  });
  
  return journalId;
};

export const getJournalForDate = async (userId, date) => {
  const dateId = getDateId(date);
  const q = query(
    collection(db, 'users', userId, 'journal'),
    where('dateId', '==', dateId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getJournalInRange = async (userId, startDate, endDate) => {
  const startDateId = getDateId(startDate);
  const endDateId = getDateId(endDate);
  
  const q = query(
    collection(db, 'users', userId, 'journal'),
    where('dateId', '>=', startDateId),
    where('dateId', '<=', endDateId),
    orderBy('dateId', 'desc'),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const updateJournalEntry = async (userId, entryId, journalData) => {
  const now = new Date();
  const entryDate = new Date(journalData.entryDate);
  const dateId = getDateId(entryDate);
  
  const docRef = doc(db, 'users', userId, 'journal', entryId);
  await updateDoc(docRef, {
    ...journalData,
    dateId,
    updatedAt: now.toISOString()
  });
};

export const deleteJournalEntry = async (userId, entryId) => {
  const docRef = doc(db, 'users', userId, 'journal', entryId);
  await deleteDoc(docRef);
};

// ============================================
// ACHIEVEMENTS
// ============================================

export const getUnlockedAchievements = async (userId) => {
  const docRef = doc(db, 'users', userId, 'achievements', 'unlocked');
  const docSnap = await getDoc(docRef);
  
  if (docSnap.exists()) {
    return docSnap.data().achievements || [];
  }
  return [];
};

export const unlockAchievement = async (userId, achievementId) => {
  const docRef = doc(db, 'users', userId, 'achievements', 'unlocked');
  const docSnap = await getDoc(docRef);
  
  let achievements = [];
  if (docSnap.exists()) {
    achievements = docSnap.data().achievements || [];
  }
  
  if (!achievements.includes(achievementId)) {
    achievements.push(achievementId);
    await setDoc(docRef, {
      achievements,
      lastUnlocked: achievementId,
      lastUnlockedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
  }
  
  return achievements;
};

export const unlockMultipleAchievements = async (userId, achievementIds) => {
  const docRef = doc(db, 'users', userId, 'achievements', 'unlocked');
  const docSnap = await getDoc(docRef);
  
  let achievements = [];
  if (docSnap.exists()) {
    achievements = docSnap.data().achievements || [];
  }
  
  const newAchievements = achievementIds.filter(id => !achievements.includes(id));
  if (newAchievements.length > 0) {
    achievements = [...achievements, ...newAchievements];
    await setDoc(docRef, {
      achievements,
      lastUnlocked: newAchievements[newAchievements.length - 1],
      lastUnlockedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
  }
  
  return newAchievements;
};

// ============================================
// COMPREHENSIVE DATA LOADING (for achievements and insights)
// ============================================

export const getAllUserData = async (userId, days = 30) => {
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - days);
  
  try {
    const [
      steps,
      heartPoints,
      water,
      meals,
      weights,
      sleep,
      journal,
      habits,
      settings
    ] = await Promise.all([
      getStepsInRange(userId, startDate, endDate),
      getHeartPointsInRange(userId, startDate, endDate),
      getWaterInRange(userId, startDate, endDate),
      getMealsInRange(userId, startDate, endDate),
      getWeightHistory(userId),
      getSleepInRange(userId, startDate, endDate),
      getJournalInRange(userId, startDate, endDate),
      getHabitHistory(userId, days),
      getUserSettings(userId)
    ]);
    
    return {
      steps: steps || [],
      heartPoints: heartPoints || [],
      water: water || [],
      meals: meals || [],
      weights: weights || [],
      sleep: sleep || [],
      journal: journal || [],
      habits: habits || [],
      userSettings: settings || {}
    };
  } catch (error) {
    console.error('Error loading all user data:', error);
    return {
      steps: [],
      heartPoints: [],
      water: [],
      meals: [],
      weights: [],
      sleep: [],
      journal: [],
      habits: [],
      userSettings: {}
    };
  }
};

